import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FluentDialog2 } from '../../../../../../../components/FluentUI/Fluent-Dialog2';
import FluentUiIcon from '../../../../../../../assets/fluent-ui-icon';
import { FluentButton } from '../../../../../../../components/FluentUI/Fluent-Button';
import { FluentDropdown, GroupedFluentDropdown } from "../../../../../../../components/FluentUI/Fluent-Dropdown";
import { FluentDropdownOptionAsObject } from "../../../../../../../components/FluentUI/Fluent-Dropdown-Option-As-Object.jsx";

import { DEFAULT_WHEN_EVENT_STATE, whenModalEventTypes, fieldInputTypes, ValueType, isWhenEventValid, isWhenEventDuplicate } from './automation-helpers';

const FormSelector = ({ allForms, eventData, setEventData, setSelectedForm, label = "Form" }) => {
    const handleFormChange = (data) => {
        setEventData((prev) => ({
            ...prev,
            EventFormId: data.optionValue,
            EventFormName: data.optionText,
            EventDynamicFieldId: null,
            EventDynamicFieldName: null,
            EventDynamicFormFieldId: null,
            EventTypeValue: "",
        }));
        setSelectedForm(allForms.find(form => form.Id === data.optionValue));
    };

    return (
        <div className='mt-2'>
            <FluentDropdownOptionAsObject
                options={allForms?.map((f) => ({
                    name: f.Label,
                    id: f.Id
                }))}
                onChange={(e, data) => handleFormChange(data)}
                label={label}
                placeholder={`Select a ${label}`}
                labelStyle="font-weight-normal"
                defaultValue={eventData.EventFormName}
            />
        </div>
    );
};

const TaskStatusContent = ({ taskStatusList, eventData, setEventData, selectedFormId }) => (
    <div className='mt-2'>
        <GroupedFluentDropdown
            label="Task Status"
            placeholder="Select a Task Status"
            groupedOptions={taskStatusList}
            defaultValue={eventData.EventTypeStatusName}
            onChange={(e, data) => {
                setEventData((prev) => ({
                    ...prev,
                    EventFormId: selectedFormId,
                    EventTypeStatusId: data.optionValue,
                    EventTypeStatusName: data.optionText,
                }));
            }}
        />
    </div>
);

const FormStatusContent = ({ allForms, formStatuses, eventData, setEventData, setSelectedForm }) => (
    <div>
        <FormSelector allForms={allForms} eventData={eventData} setEventData={setEventData} setSelectedForm={setSelectedForm} />
        <div className='mt-2'>
            <FluentDropdownOptionAsObject
                options={formStatuses?.map((status) => ({
                    name: status.Name,
                    id: status.Id
                }))}
                onChange={(e, data) => {
                    setEventData((prev) => ({
                        ...prev,
                        EventTypeStatusId: data.optionValue,
                        EventTypeStatusName: data.optionText,
                    }));
                }}
                label="Form Status"
                placeholder="Select a Form Status"
                labelStyle="font-weight-normal"
                defaultValue={eventData.EventTypeStatusName}
            />
        </div>
    </div>
);

const FormSubmissionContent = ({ allForms, eventData, setEventData, setSelectedForm }) => (
    <FormSelector allForms={allForms} eventData={eventData} setEventData={setEventData} setSelectedForm={setSelectedForm} />
);

const FieldInputContent = ({ allForms, selectedFieldInputs, eventData, setEventData, setSelectedForm }) => {

    const selectedField = selectedFieldInputs?.find(f => f.DynamicFieldId === eventData.EventDynamicFieldId);

    return (
        <>
            <FormSelector allForms={allForms} eventData={eventData} setEventData={setEventData} setSelectedForm={setSelectedForm} />
            {eventData?.EventFormId != null && (
                <div className='mt-2'>
                    <FluentDropdownOptionAsObject
                        options={selectedFieldInputs?.map((field) => ({
                            name: field.LocalLabel,
                            id: field.Id
                        }))}
                        onChange={(e, data) => {
                            let field = selectedFieldInputs?.find(f => f.Id === data.optionValue);
                            setEventData((prev) => ({
                                ...prev,
                                EventDynamicFieldId: field?.DynamicFieldId,
                                EventDynamicFieldName: field?.LocalLabel,
                                EventDynamicFormFieldId: field?.Id,
                                EventTypeValue: "",
                            }));
                        }}
                        label="Field"
                        labelStyle="font-weight-normal"
                        value={eventData?.EventDynamicFieldName}
                        isControlled={true}
                    />
                </div>
            )}
            {eventData?.EventDynamicFieldId != null && (
                <div className='mt-2'>
                    <ValueType selectedField={selectedField} eventData={eventData} setEventData={setEventData} />
                </div>
            )}
            {eventData?.EventDynamicFieldId != null && (
                <div className='mt-2'>
                    <FluentDropdown
                        label="Input to Form via"
                        placeholder="Select a value"
                        options={fieldInputTypes}
                        value={eventData?.EventTypeTrigger}
                        isControlled={true}
                        onChange={(e) => {
                            setEventData((prev) => ({
                                ...prev,
                                EventTypeTrigger: e.target.textContent || e.target.value,
                            }));
                        }}
                    />
                </div>
            )}
        </>
    );
};

export const WhenModal = ({ isOpen, onClose, onSave, taskStatusList, formStatuses, fieldsInputs, allForms, selectedFormId, isChained, selectedWhenEvent, existingWhenEvents, editingIndex }) => {
    const [eventData, setEventData] = useState(selectedWhenEvent || DEFAULT_WHEN_EVENT_STATE);
    const [selectedFieldInputs, setSelectedFieldInputs] = useState(null);
    const [selectedForm, setSelectedForm] = useState(null);
    
    useEffect(() => {
        if (eventData.EventFormId != null) {
            const form = allForms.find(f => f.Id === eventData.EventFormId);
            setSelectedForm(form);
        }
    }, []);
    useEffect(() => {
        if (selectedForm) {
            const fields = selectedForm.SubForms?.flatMap(subform => subform.Fields || []) || [];
            setSelectedFieldInputs(fields);
        }
    }, [selectedForm]);

    const eventTypeContent = {
        "Task Status Changed To": <TaskStatusContent taskStatusList={taskStatusList} eventData={eventData} setEventData={setEventData} selectedFormId={selectedFormId} />,
        "Form Status Changed To": <FormStatusContent allForms={allForms} formStatuses={formStatuses} eventData={eventData} setEventData={setEventData} setSelectedForm={setSelectedForm} />,
        "Form Submission": <FormSubmissionContent allForms={allForms} eventData={eventData} setEventData={setEventData} setSelectedForm={setSelectedForm} />,
        "Field Input": <FieldInputContent allForms={allForms} fieldsInputs={fieldsInputs} selectedFieldInputs={selectedFieldInputs} eventData={eventData} setEventData={setEventData} setSelectedForm={setSelectedForm} />
    };

    return (
        <FluentDialog2 open={isOpen}>
            <div className="task-type-dialog-title">
                <p>New When Event</p>
                <p onClick={onClose}>
                    <FluentUiIcon name={"DismissRegular"} color={"#212121"} size={20} />
                </p>
            </div>
            <div className="task-type-dialog-content">
                <div className="task-type-create-edit-field-dialog">
                    {isChained &&
                        <FluentDropdown
                            label="Selector Type"
                            placeholder="AND/OR"
                            options={["AND", "OR"]}
                            value={eventData.RelatedToCondition}
                            isControlled={true}
                            onChange={(e) => {
                                setEventData((prev) => ({
                                    ...prev,
                                    RelatedToCondition: e.target.textContent || e.target.value,
                                }));
                            }}
                        />
                    }
                    <FluentDropdown
                        label="Select an Event Type"
                        placeholder="Select an Event Type"
                        options={whenModalEventTypes}
                        value={eventData.EventType}
                        isControlled={true}
                        onChange={(e) => {
                            if (e.target.textContent === "Task Created") {
                                setEventData((prev) => ({
                                    ...DEFAULT_WHEN_EVENT_STATE,
                                    EventFormId: selectedFormId,
                                    EventType: e.target.textContent || e.target.value,
                                    RelatedToCondition: prev.RelatedToCondition
                                }));
                            }
                            else {
                                setEventData((prev) => ({
                                    ...DEFAULT_WHEN_EVENT_STATE,
                                    EventType: e.target.textContent || e.target.value,
                                    RelatedToCondition: prev.RelatedToCondition
                                }));
                            }
                        }}
                    />
                    {eventTypeContent[eventData.EventType]}
                    {isWhenEventDuplicate(eventData, existingWhenEvents, editingIndex, selectedFieldInputs) && (
                        <div className="text-danger small mt-2" style={{ whiteSpace: "nowrap", textAlign: "center" }}>This event already exists in this automation.</div>
                    )}
                </div>
            </div>
            <div className="task-type-dialog-footer">
                <FluentButton
                    onClick={onClose}
                    text={"Cancel"}
                    backgroundColor={"#0F6CBD"}
                />
                <FluentButton
                    onClick={() => onSave(eventData)}
                    appearance="primary"
                    text="Save Event"
                    backgroundColor={"#0F6CBD"}
                    disabled={!isWhenEventValid(eventData, isChained) || isWhenEventDuplicate(eventData, existingWhenEvents, editingIndex, selectedFieldInputs)}
                />
            </div>
        </FluentDialog2>
    );
};

WhenModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    taskStatusList: PropTypes.array,
    formStatuses: PropTypes.array,
    fieldsInputs: PropTypes.array,
    allForms: PropTypes.array,
    selectedFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isChained: PropTypes.bool,
    selectedWhenEvent: PropTypes.object,
    existingWhenEvents: PropTypes.array,
    editingIndex: PropTypes.number,
};

export default WhenModal;
